"use client";

import { trackAgentRevenueOfferShown } from "@/lib/analytics";
import {
  ISHVARAM_COMMERCE_SYSTEM,
  ISHVARAM_REPORT_CHECKOUT_CHANNEL,
} from "@/lib/commerce-attribution";
import { useImpressionOnce } from "@/hooks/useImpressionOnce";

/**
 * OfferImpression — a render-nothing client island that fires the `offer_seen`
 * (`agent_revenue_offer_shown`) impression EXACTLY ONCE per offer presentation.
 * Part of the offer-placement architecture (ADR-0025).
 *
 * Why this exists: every report CTA needs the same mirrored `agent_revenue_*` +
 * `commerce_*` shown payload and the same re-fire protection. Server-rendered
 * offer surfaces (e.g. CalculatorReportBridge, used by muhurat / dasha /
 * sade-sati) also need a tiny client island so they can emit the event without
 * converting the whole surface to a client component. Re-fire-proof by
 * construction via {@link useImpressionOnce} (gated on a stable string key,
 * never object identity — the 2026-06 re-fire incident class).
 */
export default function OfferImpression({
  planId,
  sourcePath,
  intent,
  placement,
  customerProblem,
  urgency = "medium",
  confidence,
  routeRankScore,
  routeRankReason,
  rankedOfferOrder,
  presentationKey,
}: {
  planId: string;
  sourcePath: string;
  intent: string;
  placement: string;
  customerProblem?: string;
  urgency?: string;
  confidence?: number;
  routeRankScore?: number;
  routeRankReason?: string;
  rankedOfferOrder?: string[];
  presentationKey?: string;
}) {
  useImpressionOnce(presentationKey ?? `${planId}|${placement}|${sourcePath}`, () => {
    trackAgentRevenueOfferShown({
      offer_type: "report",
      plan_id: planId,
      source_path: sourcePath,
      intent,
      placement,
      checkout_channel: ISHVARAM_REPORT_CHECKOUT_CHANNEL,
      commerce_system: ISHVARAM_COMMERCE_SYSTEM,
      customer_problem: customerProblem,
      urgency,
      confidence,
      route_rank_score: routeRankScore,
      route_rank_reason: routeRankReason,
      ranked_offer_order: rankedOfferOrder ?? [planId],
    });
  });
  return null;
}
